import i18n from './utils/translations';
import { I18nextProvider } from 'react-i18next';
import { IntelliProveAPIProvider } from './providers/api';
import { Outlet, useNavigate } from 'react-router-dom';
import { getAuthFromSearchParams } from './utils/path/search_params';
import { useEffect } from 'react';
import { errorEvent } from '~errors/emitter';
import { ConsoleErrorLogger } from '~errors/error_logger';
import { ErrorNavigator } from '~errors/error_navigator';
import { SettingsProvider } from '~providers/settings';
import { PostHogProvider } from 'posthog-js/react';
import { getAuthContextFromJwt } from '~utils/jwt';
import useProductAnalytics from '~hooks/product_analytics';
import useProductSurveys from '~hooks/product_surveys';
import { usePostMessageResize } from '~hooks/resize_notifier';
import _refiner from 'refiner-js';

const APIURL = import.meta.env.VITE_API_URL;

export interface iAppBootstrap {}

function AppBootstrap({}: iAppBootstrap) {
  const [authKey, authMethod] = getAuthFromSearchParams();
  const { analyticsEnabled, initialiseAnalytics, getUserReferenceId, postHogApiKey, postHogOptions } = useProductAnalytics();
  const { productSurveysEnabled, initialiseProductSurveys } = useProductSurveys();
  const navigate = useNavigate();

  usePostMessageResize();

  useEffect(() => {
    const consoleLogger = new ConsoleErrorLogger();
    const errorNavigator = new ErrorNavigator(navigate);

    const unsub = errorEvent.subscribe((evt) => {
      if (evt.silent === true) return;

      consoleLogger.handle(evt);
      errorNavigator.handle(evt);
    });

    // Global error handler
    window.onerror = (message, source, lineno, colno, error) => {
      errorEvent.emit({
        error: error,
        message: message.toString(),
        context: {
          source: source,
          lineno: lineno,
          colno: colno,
        },
      });
    };

    window.onunhandledrejection = (event) => {
      const error = event.reason;
      const message = error instanceof Error ? error.message : 'Unhandled rejection';

      const errorObject = {
        error: error,
        message: message.toString(),
        context: {
          source: "Unhandled 'catch' from Promise",
        },
      };

      errorEvent.emit(errorObject);
    };

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!analyticsEnabled) return;

    const authContext = getAuthContextFromJwt(authKey);

    if (!authContext.userId) {
      console.warn('No User ID found in auth token.');
      return;
    }

    initialiseAnalytics(authContext);

    if (productSurveysEnabled) {
      const userReferenceId = getUserReferenceId();
      initialiseProductSurveys(userReferenceId, authContext.customer);
    }
  }, []);

  return (
    <>
      <IntelliProveAPIProvider apiurl={APIURL} authentication={authKey} authenticationMethod={authMethod}>
        <I18nextProvider i18n={i18n}>
          <SettingsProvider>
            {analyticsEnabled ? (
              <PostHogProvider apiKey={postHogApiKey} options={postHogOptions}>
                <Outlet />
              </PostHogProvider>
            ) : (
              <Outlet />
            )}
          </SettingsProvider>
        </I18nextProvider>
      </IntelliProveAPIProvider>
    </>
  );
}

export default AppBootstrap;
